// src/pages/Menu/Articulacion/Directory.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Building2, MapPin, Globe2 } from "lucide-react";
import { Banner } from "../../../components/common/Banner";
import SectionTips from "../../../components/common/SectionTips";
import ArrowBack from "../../../components/common/ArrowBack";

import { listRegionalEntities } from "../../../services/regionalEntitiesService";

import EntityDetailsModal from "./Details/EntityDetailsModal";
import AddEntityModal from "./Details/AddEntityModal";

const norm = (s) => String(s ?? "").trim().toLowerCase();
const includesNorm = (a, b) => norm(a).includes(norm(b));

/* ------- UI helpers ------- */
function SegmentedScope({ value, onChange }) {
  const opts = [
    { key: "", label: "Todo" },
    { key: "regional", label: "Regional" },
    { key: "national", label: "Nacional" },
  ];
  return (
    <div className="inline-flex rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm relative z-10">
      {opts.map((o, i) => {
        const active = value === o.key;
        return (
          <button
            key={o.key + i}
            type="button"
            onClick={() => onChange(o.key)}
            className={[
              "px-3 sm:px-4 h-11 text-sm font-medium transition-all duration-200 relative z-10",
              active
                ? "text-white bg-gradient-to-r from-[#0c3b66] to-[#0488AF]"
                : "text-gray-700 hover:bg-gray-50 hover:shadow-sm",
              i !== 0 ? "border-l border-gray-200" : "",
            ].join(" ")}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Directory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Ámbito visual: '', 'regional', 'national'
  const [scope, setScope] = useState("");

  // Filtros
  const [query, setQuery] = useState("");
  const [sectorFilter, setSectorFilter] = useState(""); // '', 'public', 'private'

  // Datos
  const [entities, setEntities] = useState([]);

  // Modales
  const [entityModalOpen, setEntityModalOpen] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState(null);

  const [addEntityOpen, setAddEntityOpen] = useState(false);
  const [addVariant, setAddVariant] = useState("regional"); // 'regional' | 'national'
  const [editEntity, setEditEntity] = useState(null);
  const [highlightEntityId, setHighlightEntityId] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const entRes = await listRegionalEntities({
        q: "",
        page: 1,
        limit: 1000,
      });
      const rawEntities = Array.isArray(entRes?.data)
        ? entRes.data
        : Array.isArray(entRes)
        ? entRes
        : [];
      setEntities(rawEntities);
    } catch (e) {
      console.error(e);
      setError("No se pudieron cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const q = norm(query);

  const applyFilters = (list) =>
    list
      .filter((e) => {
        const textOK =
          !q ||
          includesNorm(e.name, q) ||
          includesNorm(e.region, q) ||
          includesNorm(e.description, q) ||
          (Array.isArray(e.workers) &&
            e.workers.some(
              (w) =>
                includesNorm(w.name, q) ||
                includesNorm(w.role, q) ||
                includesNorm(w.email, q)
            ));
        const sectorOK =
          !sectorFilter || (e.sector || "") === sectorFilter;
        return textOK && sectorOK;
      })
      .sort((a, b) => a.name.localeCompare(b.name));

  const regionals = useMemo(
    () =>
      applyFilters(
        entities.filter(
          (e) => (e.scope || "regional") === "regional"
        )
      ),
    [entities, q, sectorFilter]
  );
  const nationals = useMemo(
    () => applyFilters(entities.filter((e) => e.scope === "national")),
    [entities, q, sectorFilter]
  );

  // Totales (respetan filtros + ámbito)
  const totalRegionals = regionals.length;
  const totalNationals = nationals.length;
  const totalVisible =
    scope === "regional"
      ? totalRegionals
      : scope === "national"
      ? totalNationals
      : totalRegionals + totalNationals;

  // ver detalle
  const openEntity = useCallback((entity) => {
    if (!entity || !entity.id) return;
    setSelectedEntityId(entity.id);
    setEntityModalOpen(true);
  }, []);

  const clearFilters = () => {
    setQuery("");
    setSectorFilter("");
    setScope("");
  };

  // resaltar
  useEffect(() => {
    if (!highlightEntityId) return;
    const el = document.getElementById(
      `entity-${highlightEntityId}`
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(
        () => setHighlightEntityId(null),
        2500
      );
      return () => clearTimeout(t);
    }
  }, [highlightEntityId]);

  const hasAnyFilter = Boolean(query || sectorFilter || scope);

  const Card = ({ e }) => {
    const isHighlighted = highlightEntityId === e.id;
    const scopeBadge =
      (e.scope || "regional") === "national"
        ? {
            label: "Nacional",
            tone: "bg-blue-50 text-[#0b3a66] border-blue-100",
          }
        : {
            label: "Regional",
            tone: "bg-emerald-50 text-emerald-700 border-emerald-100",
          };

    const handleClick = useCallback((event) => {
      event.preventDefault();
      event.stopPropagation();
      openEntity(e);
    }, [e, openEntity]);

    return (
      <button
        key={e.id}
        id={`entity-${e.id}`}
        type="button"
        onClick={handleClick}
        onMouseDown={(e) => e.preventDefault()}
        className={`w-full text-left bg-white rounded-2xl border shadow-sm hover:shadow-lg hover:border-[#0488AF]/30 active:scale-[0.98] transition-all duration-200 p-6 focus:outline-none cursor-pointer relative ${
          isHighlighted
            ? "border-[#0488AF] ring-2 ring-[#0488AF]/60 z-10"
            : "border-gray-200 z-0"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {e.imageUrl ? (
              <img
                src={e.imageUrl}
                alt={e.name}
                className="w-12 h-12 rounded-xl object-contain bg-white ring-1 ring-gray-200 p-1 flex-shrink-0"
                draggable="false"
              />
            ) : (
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 shrink-0">
                <Building2 className="w-6 h-6 text-[#0488AF]" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h4 className="text-lg font-semibold text-gray-900 truncate">
                {e.name}
              </h4>
              <div className="text-sm text-gray-600 flex items-center gap-2 flex-wrap mt-1">
                <span className="inline-flex items-center gap-1">
                  <MapPin size={14} className="opacity-70" />
                  {e.region || "Ubicación no especificada"}
                </span>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border ${scopeBadge.tone}`}
                >
                  <Globe2 size={12} /> {scopeBadge.label}
                </span>
                {e.sector && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-50 border border-gray-200">
                    {e.sector === "public"
                      ? "Público"
                      : "Privado"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </button>
    );
  };

  const Section = ({ title, items }) => {
    if (!items.length) return null;
    const count = items.length;
    return (
      <section className="mb-10">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <span>{title}</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs bg-[#E5F0FB] text-[#0c3b66] border border-[#c1d7f5] shadow-sm">
              <span className="w-2 h-2 rounded-full bg-[#0488AF]" />
              <span className="tabular-nums font-semibold">
                {count}
              </span>
              <span className="font-medium">
                {count === 1
                  ? "entidad visible"
                  : "entidades visibles"}
              </span>
            </span>
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-5">
          {items.map((e) => (
            <Card key={e.id} e={e} />
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-6 space-y-6">
      {/* Banner arriba */}
      <div className="relative">
        <div className="px-1 pb-2">
          <ArrowBack />
        </div>
        <Banner
          title="Directorio Digital de Contactos"
          message="Directorio digital: instituciones y entidades (nacionales y regionales)."
        />
      </div>

      <SectionTips
        section="el directorio digital de contactos"
        buttonTitle="¿Cómo usar esta sección?"
        tipsTitle="¿Cómo usar el directorio?"
        tipsList={[
          'Las tarjetas que ves en esta sección son entidades (instituciones, corporaciones, empresas, etc.)',
          'Los contactos se ordenan en subdivisiones dentro de cada entidad, para registrar un contacto de una institución que no está en la lista primero debes crear la entidad.',
          'Haz clic en una entidad para ver su información, ver sus contactos y agregar nuevos.',
          'Puedes filtrar por ámbito (Regional o Nacional), por sector (Público o Privado) y por palabras en el nombre, ubicación o descripción de la entidad.',
          'Dentro de cada entidad también encontrarás filtros para los contactos.',
        ]}
        finalNote=""
        color="#0488AF"
        videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766417259/Video_Tutorial_m%C3%B3dulo_Articulaci%C3%B3n_P-P_-_Directorio_de_Contactos_gkpy28.mp4"
        manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412940/Manual_de_usuario_m%C3%B3dulo_de_articulaci%C3%B3n_P-P_nfoqgo.pdf"
      />

      {/* Barra de controles */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3">
          {/* Primera fila: ámbito, búsqueda, sector, botón */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            {/* Ámbito */}
            <SegmentedScope value={scope} onChange={setScope} />

            {/* Buscar + Sector + acciones */}
            <div className="flex-1 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
              {/* Buscar */}
              <div className="relative flex-1 min-w-[220px]">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nombre, ubicación, descripción…"
                  className="w-full h-11 pl-10 pr-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A] placeholder:text-gray-400"
                />
              </div>

              {/* Sector + acciones */}
              <div className="flex items-center gap-3">
                {/* Sector */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#09274A] hidden sm:inline tracking-wide uppercase">
                    Sector
                  </span>
                  <select
                    value={sectorFilter}
                    onChange={(e) =>
                      setSectorFilter(e.target.value)
                    }
                    className="h-11 px-3 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none w-32 sm:w-40 text-sm text-[#09274A]"
                    title="Sector"
                  >
                    <option value="">Todos</option>
                    <option value="public">Público</option>
                    <option value="private">Privado</option>
                  </select>
                </div>

                {/* Acciones derecha */}
                <div className="flex items-center justify-end gap-3">
                  {hasAnyFilter && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="inline-flex items-center justify-center h-9 px-4 rounded-full border border-[#DC3465] text-[#DC3465] text-xs font-semibold tracking-wide uppercase hover:bg-[#C42251] hover:text-white transition-colors"
                      title="Limpiar filtros"
                    >
                      Limpiar filtros
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setEditEntity(null);
                      setAddVariant("regional");
                      setAddEntityOpen(true);
                    }}
                    className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-all duration-200 relative z-10"
                  >
                    <Plus size={18} /> Añadir entidad
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Segunda fila: contador total, debajo de los filtros */}
          <div className="pt-1">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-2xl bg-[#E5F4FA] border border-[#7cc3dd] shadow-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-[#0488AF]" />
              <div className="flex flex-col leading-tight">
                <span className="text-[11px] sm:text-xs font-medium text-gray-600">
                  Total entidades visibles
                </span>
                <span className="text-lg sm:text-xl font-bold text-[#0488AF] tabular-nums">
                  {totalVisible}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      )}

      {/* CONTENIDO */}
      {loading ? (
        <div className="text-center text-gray-500 py-12">
          Cargando…
        </div>
      ) : (
        <>
          {(scope === "" || scope === "regional") && (
            <Section
              title="Entidades regionales"
              items={regionals}
            />
          )}
          {(scope === "" || scope === "national") && (
            <Section
              title="Entidades nacionales"
              items={nationals}
            />
          )}

          {scope !== "regional" &&
            scope !== "national" &&
            !regionals.length &&
            !nationals.length && (
              <div className="text-gray-500 py-10">
                No hay entidades
              </div>
            )}
          {scope === "regional" && !regionals.length && (
            <div className="text-gray-500 py-10">
              No hay entidades regionales
            </div>
          )}
          {scope === "national" && !nationals.length && (
            <div className="text-gray-500 py-10">
              No hay entidades nacionales
            </div>
          )}
        </>
      )}

      {/* Detalle entidad */}
      <EntityDetailsModal
        open={entityModalOpen}
        entityId={selectedEntityId}
        onClose={() => setEntityModalOpen(false)}
        onChanged={async (changed) => {
          await loadData(); // refresco duro
          if (changed?.id) setHighlightEntityId(changed.id);
        }}
        onEditEntity={(entity) => {
          setEntityModalOpen(false);
          setEditEntity(entity);
          setAddVariant(
            entity?.scope === "national" ? "national" : "regional"
          );
          setAddEntityOpen(true);
        }}
      />

      {/* Crear/editar entidad */}
      <AddEntityModal
        open={addEntityOpen}
        onClose={() => {
          setAddEntityOpen(false);
          setEditEntity(null);
        }}
        variant={addVariant}
        entity={editEntity}
        onSaved={async (saved) => {
          setAddEntityOpen(false);
          setEditEntity(null);
          if (saved?.id) {
            setEntities((prev) => {
              const exists = prev.some((e) => e.id === saved.id);
              const item = { workersCount: 0, ...saved };
              return exists
                ? prev.map((e) =>
                    e.id === saved.id ? { ...e, ...item } : e
                  )
                : [item, ...prev];
            });
            setScope(saved.scope || "");
            setHighlightEntityId(saved.id);
          }
          await loadData();
        }}
      />
    </div>
  );
}
